/**
 * AvatarBridge (2026-05-23 — protocol spec: AVATAR_BRIDGE_PROTOCOL.md)
 *
 * Singleton service that connects the Preston avatar iframe (served from
 * each account's VM) to the prest0.ai platform shell's thread/chat system.
 *
 * Flow:
 *   1. Avatar iframe sends window.parent.postMessage({type:'prest0n.bridge.v1.request', ...}).
 *   2. AvatarBridge.onMessage catches it, verifies event.origin matches the
 *      allowed avatar-VM origin for this user (set via setAllowedOrigins()),
 *      dispatches to a registered handler for the method, and posts back
 *      {type:'prest0n.bridge.v1.response', requestId, ok, result, error}.
 *   3. Platform-side hooks (useAvatarBridge, etc.) call `broadcastEvent()`
 *      to push unsolicited notifications (workflow started, clarifying
 *      question, deliverable-ready, etc.) back to the avatar.
 *
 * Security:
 *   - event.origin MUST match one entry in the allowed origins list.
 *   - Unknown origins: silently reject, log to console.warn, never dispatch.
 *   - Unknown methods: return { ok: false, error: 'method-not-supported' }.
 *   - This service is browser-only (no Node-side usage); never imported
 *     from functions/ or other server paths.
 *
 * Integration pattern:
 *   - AgentActivity.tsx or similar mounts the avatar iframe, then:
 *       AvatarBridge.registerIframe(iframeRef.current);
 *       AvatarBridge.setAllowedOrigins([vmBaseUrl]);
 *       AvatarBridge.registerHandlers({ newThread, switchThread, ... });
 *     And tears down on unmount.
 *   - Any component broadcasting events calls:
 *       AvatarBridge.broadcastEvent({ event: 'workflow.started', threadId, ... });
 *
 * See AVATAR_BRIDGE_PROTOCOL.md for envelope schema and event taxonomy.
 */

export type BridgeMethod =
  | 'newThread'
  | 'switchThread'
  | 'submitToThread'
  | 'listThreads'
  | 'getActiveThread'
  | 'getWorkflowEvents';

export interface BridgeRequestMessage {
  type: 'prest0n.bridge.v1.request';
  requestId: string;
  method: BridgeMethod;
  params: Record<string, unknown>;
}

export interface BridgeResponseMessage {
  type: 'prest0n.bridge.v1.response';
  requestId: string;
  ok: boolean;
  result?: Record<string, unknown> | null;
  error?: string | null;
}

export type BridgeEventName =
  | 'thread.created'
  | 'thread.switched'
  | 'thread.message.delta'
  | 'thread.message.completed'
  | 'thread.deliverable.ready'
  | 'thread.clarifying-question'
  | 'workflow.started'
  | 'workflow.progress'
  | 'workflow.completed'
  | 'workflow.failed'
  | 'wallet.balance.low';

export interface BridgeEventMessage {
  type: 'prest0n.bridge.v1.event';
  event: BridgeEventName;
  threadId: string;
  timestamp: string;
  payload: Record<string, unknown>;
}

export type BridgeHandlerResult =
  | Record<string, unknown>
  | Promise<Record<string, unknown>>;

export type BridgeHandlers = {
  newThread: (params: { title?: string; focus?: string }) => BridgeHandlerResult;
  switchThread: (params: { threadId: string }) => BridgeHandlerResult;
  submitToThread: (params: { threadId: string; text: string; kind?: 'user' | 'system' }) => BridgeHandlerResult;
  listThreads: (params: Record<string, unknown>) => BridgeHandlerResult;
  getActiveThread: (params: Record<string, unknown>) => BridgeHandlerResult;
  getWorkflowEvents: (params: { threadIds?: string[]; sinceTimestamp?: string }) => BridgeHandlerResult;
};

interface RegisteredTarget {
  iframe: HTMLIFrameElement;
  label?: string;
}

const TYPE_REQUEST = 'prest0n.bridge.v1.request';
const TYPE_RESPONSE = 'prest0n.bridge.v1.response';
const TYPE_EVENT = 'prest0n.bridge.v1.event';

function isBridgeRequest(d: unknown): d is BridgeRequestMessage {
  if (!d || typeof d !== 'object') return false;
  const rec = d as Record<string, unknown>;
  return (
    rec.type === TYPE_REQUEST
    && typeof rec.requestId === 'string'
    && typeof rec.method === 'string'
  );
}

function normalizeOrigins(origins: string[]): string[] {
  const out: string[] = [];
  for (const o of origins) {
    if (!o) continue;
    // Strip trailing slash and any port suffix — browser event.origin
    // format is scheme://host[:port] with no trailing slash ever, but we
    // don't want to fail a match just because the configured URL had one.
    const cleaned = o.replace(/\/+$/, '');
    if (!out.includes(cleaned)) out.push(cleaned);
  }
  return out;
}

class AvatarBridgeImpl {
  private allowedOrigins: string[] = [];
  private iframe: HTMLIFrameElement | null = null;
  private handlers: Partial<BridgeHandlers> = {};
  private listener: ((ev: MessageEvent) => void) | null = null;
  private listenerInstalled = false;

  /**
   * Register the avatar iframe the bridge should post messages to.
   * Replaces any previously-registered iframe. Pass null to deregister.
   */
  registerIframe(iframe: HTMLIFrameElement | null, label?: string): void {
    this.iframe = iframe;
    if (iframe && label) {
      try { iframe.dataset.bridgeLabel = label; } catch { /* ignore */ }
      void ({} as RegisteredTarget).label; // keep type used
    }
    this.ensureListenerInstalled();
    try {
      // Log registration so we can trace the target in production.
      // eslint-disable-next-line no-console
      console.log(
        `[AvatarBridge] registerIframe: %s`,
        iframe ? (iframe.dataset.bridgeLabel || iframe.src.slice(0, 80) || '(no src)') : '(none)'
      );
    } catch { /* ignore */ }
  }

  /**
   * Set the list of allowed origins for incoming bridge requests.
   * Typically one entry: the current user's avatar VM base URL
   * (https://{vmHost}) from Firestore accounts/{accountId}/vm/main.baseUrl.
   *
   * For internal Prest0 users (Justin/Miles/Prest0n), pass the internal VM
   * origin (DEFAULT_INTERNAL_BASE from useRealtimeDesktopTarget).
   *
   * Messages with event.origin NOT in this list are silently rejected.
   *
   @example
    AvatarBridge.setAllowedOrigins(['https://35-238-149-88.sslip.io']);
   */
  setAllowedOrigins(origins: string[]): void {
    this.allowedOrigins = normalizeOrigins(origins);
    // eslint-disable-next-line no-console
    console.log(`[AvatarBridge] setAllowedOrigins:`, this.allowedOrigins);
  }

  /**
   * Register per-method handlers. Handlers may return sync object or
   * Promise<object>. If a handler throws or rejects, the promise resolves
   * to { ok: false, error: message }.
   *
   * Partial registration is allowed; unregistered methods return the
   * 'method-not-supported' error.
   */
  registerHandlers(handlers: Partial<BridgeHandlers>): void {
    this.handlers = { ...this.handlers, ...handlers };
  }

  /**
   * Clear handlers (used on unmount or re-render reset). Optional: pass
   * method names to clear specific handlers; otherwise clears all.
   */
  clearHandlers(methods?: BridgeMethod[]): void {
    if (!methods) {
      this.handlers = {};
      return;
    }
    for (const m of methods) delete this.handlers[m];
  }

  /**
   * Push an unsolicited event to the avatar iframe. Used by workflow
   * handlers to signal progress/completion/clarifying-question.
   */
  broadcastEvent(params: {
    event: BridgeEventName;
    threadId: string;
    payload?: Record<string, unknown>;
  }): void {
    if (!this.iframe) {
      // No target registered yet — drop silently (startup race).
      return;
    }
    const iframeWindow = this.iframe.contentWindow;
    if (!iframeWindow) return;
    const msg: BridgeEventMessage = {
      type: TYPE_EVENT,
      event: params.event,
      threadId: params.threadId,
      timestamp: new Date().toISOString(),
      payload: params.payload || {},
    };
    try {
      iframeWindow.postMessage(msg, '*');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[AvatarBridge] broadcastEvent failed:', e);
    }
  }

  /**
   * Post a structured response to a specific request (called internally
   * by the dispatch loop, exposed for advanced use/testing).
   */
  postResponse(target: Window, response: BridgeResponseMessage): void {
    try {
      target.postMessage(response, '*');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[AvatarBridge] postResponse failed:', e);
    }
  }

  /** Install the window-level message listener (idempotent). */
  private ensureListenerInstalled(): void {
    if (this.listenerInstalled) return;
    this.listener = (ev: MessageEvent) => this.onWindowMessage(ev);
    window.addEventListener('message', this.listener);
    this.listenerInstalled = true;
  }

  /**
   * Active thread id accessor. The platform-shell thread store registers
   * a getter at mount time so workflow handlers outside React (e.g.
   * src/services/agenticWorkflows/handlers.ts) can read the current
   * active thread id when broadcasting events.
   *
   * Returns null when unset or when the thread store is not yet mounted.
   * Callers should handle null gracefully (pass "unknown" threadId or
   * skip the event).
   */
  private activeThreadIdGetter: (() => string | null) | null = null;
  registerActiveThreadIdGetter(getter: () => string | null): void {
    this.activeThreadIdGetter = getter;
  }
  getActiveThreadId(): string | null {
    try {
      return this.activeThreadIdGetter ? this.activeThreadIdGetter() : null;
    } catch {
      return null;
    }
  }

  /**
   * Teardown: remove the listener, clear registration. Idempotent.
   * Called on component unmount.
   */
  dispose(): void {
    if (this.listenerInstalled && this.listener) {
      try {
        window.removeEventListener('message', this.listener);
      } catch (_e) {
        // SSR / non-browser envs: window not defined is a normal fallback.
      }
      this.listenerInstalled = false;
      this.listener = null;
    }
    this.iframe = null;
    this.allowedOrigins = [];
    this.handlers = {};
    this.activeThreadIdGetter = null;
    // eslint-disable-next-line no-console
    console.log('[AvatarBridge] disposed');
  }

  // ─── internal dispatch ────────────────────────────────────────────────

  private onWindowMessage(ev: MessageEvent): void {
    // Origin filter — silent-reject on mismatched origin. Defense-in-depth
    // check so even if we receive unrelated postMessage traffic from other
    // embedders on this page, we don't dispatch them as bridge requests.
    if (typeof ev.origin !== 'string') return;
    if (this.allowedOrigins.length > 0 && !this.allowedOrigins.includes(ev.origin)) {
      return;
    }
    const d = ev.data;
    if (!isBridgeRequest(d)) return;
    // Origin matches AND it's a well-formed bridge request — dispatch.
    void this.dispatchRequest(ev.source as Window | null, d);
  }

  private async dispatchRequest(
    source: Window | null,
    req: BridgeRequestMessage
  ): Promise<void> {
    const target = source;
    const handler = this.handlers[req.method];
    if (!handler) {
      this.sendRespondTo(target, {
        type: TYPE_RESPONSE,
        requestId: req.requestId,
        ok: false,
        error: `method-not-supported: ${req.method}`,
      });
      return;
    }
    try {
      // `handler` is typed as the INTERSECTION of all BridgeHandlers values
      // because TS can't narrow it by req.method without a runtime check.
      // Each handler validates its own params; we pass through the raw
      // Record<string, unknown> and cast at the boundary.
      const raw = await (handler as unknown as (
        p: Record<string, unknown>
      ) => BridgeHandlerResult)(req.params || {});
      if (raw && typeof raw === 'object') {
        this.sendRespondTo(target, {
          type: TYPE_RESPONSE,
          requestId: req.requestId,
          ok: true,
          result: raw,
        });
      } else {
        this.sendRespondTo(target, {
          type: TYPE_RESPONSE,
          requestId: req.requestId,
          ok: false,
          error: 'handler returned non-object result',
        });
      }
    } catch (e) {
      const err = e instanceof Error ? e.message : String(e);
      this.sendRespondTo(target, {
        type: TYPE_RESPONSE,
        requestId: req.requestId,
        ok: false,
        error: err,
      });
    }
  }

  private sendRespondTo(target: Window | null, resp: BridgeResponseMessage): void {
    if (!target) {
      // Fallback: post to the iframe directly (in case ev.source is null).
      if (this.iframe && this.iframe.contentWindow) {
        try { this.iframe.contentWindow.postMessage(resp, '*'); } catch { /* ignore */ }
      }
      return;
    }
    try { target.postMessage(resp, '*'); } catch { /* ignore */ }
  }
}

export const AvatarBridge: AvatarBridgeImpl = new AvatarBridgeImpl();
export default AvatarBridge;
